import React, { useCallback, useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Cog6ToothIcon,
  ShieldCheckIcon,
  BellIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  UserGroupIcon,
  KeyIcon,
  ArrowPathIcon,
  CloudArrowUpIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import tenantService from '../../services/tenantService';
import deviseService from '../../services/deviseService';
import loanService from '../../services/loanService';
import importExportService from '../../services/importExportService';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CurrencyContext';

const emptyDeviseForm = {
  name: '',
  description: '',
  pays: '',
  isLocale: false,
};

const emptyProductForm = {
  name: '',
  code: '',
  description: '',
  product_type: 'individual',
  min_amount: '',
  max_amount: '',
  interest_rate: '',
  interest_type: 'flat',
  default_payment_frequency: 'monthly',
  term_min: '',
  term_max: '',
  grace_period: 0,
  penalty_rate: 0,
  processing_fee_rate: 0,
  insurance_fee_rate: 0,
  required_savings_percent: 0,
  collateral_required: false,
  guarantor_required: false,
  approval_level: 'branch',
  is_active: true,
};

const institutionTemplateOptions = [
  { value: 'microfinance', label: 'Micro-finance' },
  { value: 'microcredit', label: 'Micro-crédit' },
  { value: 'mutual', label: 'Mutuelle' },
  { value: 'microbank', label: 'Micro-banque' },
  { value: 'cooperative', label: 'Coopérative financière' },
];

const timezoneOptions = [
  { value: 'America/Port-au-Prince', label: 'Haïti - Port-au-Prince' },
  { value: 'America/New_York', label: 'États-Unis - New York' },
  { value: 'America/Toronto', label: 'Canada - Toronto' },
  { value: 'America/Montreal', label: 'Canada - Montréal' },
  { value: 'America/Mexico_City', label: 'Mexique - Mexico' },
  { value: 'America/Panama', label: 'Panama' },
  { value: 'America/Bogota', label: 'Colombie - Bogotá' },
  { value: 'America/Lima', label: 'Pérou - Lima' },
  { value: 'America/Caracas', label: 'Venezuela - Caracas' },
  { value: 'America/Santo_Domingo', label: 'République dominicaine - Santo Domingo' },
  { value: 'America/Havana', label: 'Cuba - La Havane' },
  { value: 'America/Jamaica', label: 'Jamaïque - Kingston' },
  { value: 'America/Guadeloupe', label: 'Guadeloupe' },
  { value: 'America/Martinique', label: 'Martinique' },
  { value: 'America/Puerto_Rico', label: 'Porto Rico - San Juan' },
  { value: 'America/Port_of_Spain', label: 'Trinité-et-Tobago - Port of Spain' },
  { value: 'America/Sao_Paulo', label: 'Brésil - São Paulo' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Argentine - Buenos Aires' },
  { value: 'America/Santiago', label: 'Chili - Santiago' },
  { value: 'Africa/Abidjan', label: "Côte d'Ivoire - Abidjan" },
  { value: 'Africa/Accra', label: 'Ghana - Accra' },
  { value: 'Africa/Dakar', label: 'Sénégal - Dakar' },
  { value: 'Africa/Bamako', label: 'Mali - Bamako' },
  { value: 'Africa/Conakry', label: 'Guinée - Conakry' },
  { value: 'Africa/Lome', label: 'Togo - Lomé' },
  { value: 'Africa/Porto-Novo', label: 'Bénin - Porto-Novo' },
  { value: 'Africa/Ouagadougou', label: 'Burkina Faso - Ouagadougou' },
  { value: 'Africa/Lagos', label: 'Nigeria - Lagos' },
  { value: 'Africa/Kinshasa', label: 'RDC - Kinshasa' },
  { value: 'Africa/Douala', label: 'Cameroun - Douala' },
  { value: 'Africa/Libreville', label: 'Gabon - Libreville' },
  { value: 'Africa/Casablanca', label: 'Maroc - Casablanca' },
  { value: 'Africa/Tunis', label: 'Tunisie - Tunis' },
  { value: 'Africa/Algiers', label: 'Algérie - Alger' },
  { value: 'Africa/Cairo', label: 'Égypte - Le Caire' },
  { value: 'Africa/Nairobi', label: 'Kenya - Nairobi' },
  { value: 'Africa/Johannesburg', label: 'Afrique du Sud - Johannesburg' },
  { value: 'Europe/Paris', label: 'France - Paris' },
  { value: 'UTC', label: 'UTC' },
];

const TenantSettings = () => {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'backup' ? 'backup' : 'general';
  const { user, hasPermission } = useAuth();
  const { formatCurrency } = useCurrency();
  const [settings, setSettings] = useState(null);
  const [devises, setDevises] = useState([]);
  const [loanProducts, setLoanProducts] = useState([]);
  const [deviseForm, setDeviseForm] = useState(emptyDeviseForm);
  const [editingDeviseId, setEditingDeviseId] = useState(null);
  const [productForm, setProductForm] = useState(emptyProductForm);
  const [editingProductId, setEditingProductId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedImportFile, setSelectedImportFile] = useState(null);
  const [selectedTemplateType, setSelectedTemplateType] = useState('microfinance');
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const { showSuccess, showError } = useToast();
  const roleName = user?.role?.name?.toLowerCase() || '';
  const isGlobalUser = (
    hasPermission('global_settings') ||
    hasPermission('manage_tenants') ||
    roleName === 'global_admin' ||
    roleName === 'super_admin'
  );
  const canManageSettings = isGlobalUser || hasPermission('system_settings');
  const canManageUsers = isGlobalUser || hasPermission('manage_users');
  const canManageAgencies = isGlobalUser || hasPermission('manage_agencies');
  const canImportExport = isGlobalUser || hasPermission('system_settings');

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await tenantService.getTenantSettings();
      setSettings(data);
    } catch (error) {
      showError('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const fetchDevises = useCallback(async () => {
    try {
      const data = await deviseService.getAllDevises();
      setDevises(data);
    } catch (error) {
      showError('Erreur lors du chargement des devises');
    }
  }, [showError]);

  const fetchLoanProducts = useCallback(async () => {
    try {
      const data = await loanService.getLoanProducts({ include_inactive: true });
      setLoanProducts(data);
    } catch (error) {
      showError('Erreur lors du chargement des produits de prêt');
    }
  }, [showError]);

  useEffect(() => {
    if (canManageSettings) {
      fetchSettings();
      fetchDevises();
      fetchLoanProducts();
    } else {
      setLoading(false);
    }
  }, [canManageSettings, fetchSettings, fetchDevises, fetchLoanProducts]);

  useEffect(() => {
    if (searchParams.get('tab') === 'backup') {
      setActiveTab('backup');
    }
  }, [searchParams]);

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await tenantService.updateTenantSettings(settings);
      showSuccess('Paramètres sauvegardés avec succès');
    } catch (error) {
      showError('Erreur lors de la sauvegarde des paramètres');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (category, field, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  const handleBackup = async () => {
    try {
      await tenantService.backupTenantData();
      showSuccess('Sauvegarde initiée avec succès');
    } catch (error) {
      showError('Erreur lors de la sauvegarde');
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = async () => {
    try {
      setExporting(true);
      const blob = await importExportService.downloadTemplate(selectedTemplateType);
      downloadBlob(blob, `modele_import_${selectedTemplateType}.xlsx`);
      showSuccess('Modèle téléchargé');
    } catch (error) {
      showError('Erreur lors du téléchargement du modèle');
    } finally {
      setExporting(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await importExportService.exportData();
      const date = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `microfinance_export_${date}.xlsx`);
      showSuccess('Export Excel réussi');
    } catch (error) {
      showError('Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async () => {
    if (!selectedImportFile) {
      showError('Sélectionnez un fichier Excel à importer');
      return;
    }

    try {
      setImporting(true);
      setImportResult(null);
      const result = await importExportService.importData(selectedImportFile);
      setImportResult(result);
      showSuccess(result.message || 'Import terminé');
    } catch (error) {
      const result = error.response?.data;
      setImportResult(result || null);
      showError(result?.message || 'Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  const resetDeviseForm = () => {
    setDeviseForm(emptyDeviseForm);
    setEditingDeviseId(null);
  };

  const handleSaveDevise = async () => {
    if (!deviseForm.name.trim()) {
      showError('Le code de la devise est requis');
      return;
    }

    try {
      const payload = {
        ...deviseForm,
        name: deviseForm.name.trim().toUpperCase(),
      };

      if (editingDeviseId) {
        await deviseService.updateDevise(editingDeviseId, payload);
        showSuccess('Devise mise à jour');
      } else {
        await deviseService.createDevise(payload);
        showSuccess('Devise créée');
      }

      resetDeviseForm();
      fetchDevises();
    } catch (error) {
      showError(error.response?.data?.message || 'Erreur lors de la sauvegarde de la devise');
    }
  };

  const handleEditDevise = (devise) => {
    setEditingDeviseId(devise.id);
    setDeviseForm({
      name: devise.name || '',
      description: devise.description || '',
      pays: devise.pays || '',
      isLocale: !!devise.isLocale,
    });
    setActiveTab('currencies');
  };

  const handleDeleteDevise = async (deviseId) => {
    if (!window.confirm('Supprimer cette devise ?')) return;

    try {
      await deviseService.deleteDevise(deviseId);
      showSuccess('Devise supprimée');
      fetchDevises();
    } catch (error) {
      showError(error.response?.data?.message || 'Erreur lors de la suppression de la devise');
    }
  };

  const resetProductForm = () => {
    setProductForm(emptyProductForm);
    setEditingProductId(null);
  };

  const productPayload = () => ({
    ...productForm,
    code: productForm.code.trim().toUpperCase(),
    min_amount: Number(productForm.min_amount || 0),
    max_amount: Number(productForm.max_amount || 0),
    interest_rate: Number(productForm.interest_rate || 0),
    default_payment_frequency: productForm.default_payment_frequency,
    term_min: Number(productForm.term_min || 0),
    term_max: Number(productForm.term_max || 0),
    grace_period: Number(productForm.grace_period || 0),
    penalty_rate: Number(productForm.penalty_rate || 0),
    processing_fee_rate: Number(productForm.processing_fee_rate || 0),
    insurance_fee_rate: Number(productForm.insurance_fee_rate || 0),
    required_savings_percent: Number(productForm.required_savings_percent || 0),
    collateral_required: !!productForm.collateral_required,
    guarantor_required: !!productForm.guarantor_required,
  });

  const handleSaveLoanProduct = async () => {
    const payload = productPayload();
    if (!payload.name || !payload.code || !payload.min_amount || !payload.max_amount || !payload.interest_rate || !payload.term_min || !payload.term_max) {
      showError('Remplissez les champs requis du produit de prêt');
      return;
    }
    if (payload.min_amount > payload.max_amount || payload.term_min > payload.term_max) {
      showError('Vérifiez les limites de montant et de durée');
      return;
    }

    try {
      if (editingProductId) {
        await loanService.updateLoanProduct(editingProductId, payload);
        showSuccess('Produit de prêt mis à jour');
      } else {
        await loanService.createLoanProduct(payload);
        showSuccess('Produit de prêt créé');
      }

      resetProductForm();
      fetchLoanProducts();
    } catch (error) {
      showError(error.response?.data?.message || 'Erreur lors de la sauvegarde du produit');
    }
  };

  const handleEditLoanProduct = (product) => {
    setEditingProductId(product.id);
    setProductForm({
      name: product.name || '',
      code: product.code || '',
      description: product.description || '',
      product_type: product.product_type || 'individual',
      min_amount: product.min_amount ?? '',
      max_amount: product.max_amount ?? '',
      interest_rate: product.interest_rate ?? '',
      interest_type: product.interest_type || 'flat',
      default_payment_frequency: product.default_payment_frequency || 'monthly',
      term_min: product.term_min ?? '',
      term_max: product.term_max ?? '',
      grace_period: product.grace_period ?? 0,
      penalty_rate: product.penalty_rate ?? 0,
      processing_fee_rate: product.processing_fee_rate ?? 0,
      insurance_fee_rate: product.insurance_fee_rate ?? 0,
      required_savings_percent: product.required_savings_percent ?? 0,
      collateral_required: !!product.collateral_required,
      guarantor_required: !!product.guarantor_required,
      approval_level: product.approval_level || 'branch',
      is_active: product.is_active !== false,
    });
    setActiveTab('loan-products');
  };

  const handleDeleteLoanProduct = async (productId) => {
    if (!window.confirm('Supprimer ce produit de prêt ?')) return;

    try {
      await loanService.deleteLoanProduct(productId);
      showSuccess('Produit supprimé');
      fetchLoanProducts();
    } catch (error) {
      showError(error.response?.data?.message || 'Erreur lors de la suppression du produit');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-2 text-sm text-gray-600">Chargement des paramètres...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    ...(canManageSettings ? [
      { id: 'general', name: 'Général', icon: Cog6ToothIcon },
      { id: 'finance', name: 'Finance', icon: CurrencyDollarIcon },
      { id: 'currencies', name: 'Devises', icon: CurrencyDollarIcon },
      { id: 'loan-products', name: 'Produits de prêt', icon: DocumentTextIcon },
      { id: 'security', name: 'Sécurité', icon: ShieldCheckIcon },
      { id: 'notifications', name: 'Notifications', icon: BellIcon },
    ] : []),
    ...(canManageUsers || canManageAgencies || isGlobalUser ? [
      { id: 'access', name: 'Accès', icon: UserGroupIcon },
    ] : []),
    ...(canImportExport ? [
      { id: 'backup', name: 'Import / Export', icon: CloudArrowUpIcon },
    ] : []),
    ...(isGlobalUser ? [
      { id: 'global', name: 'Global', icon: KeyIcon },
    ] : []),
  ];

  const selectedTab = tabs.some(tab => tab.id === activeTab) ? activeTab : tabs[0]?.id;
  const currency = settings?.general?.currency || 'HTG';
  const currencyOptions = [
    ...new Map(devises.map((devise) => [devise.name, devise])).values()
  ];
  const hasConfiguredCurrency = currencyOptions.some((devise) => devise.name === currency);

  if (tabs.length === 0) {
    return (
      <div className="card">
        <div className="p-6">
          <h1 className="text-xl font-semibold text-gray-900">Paramètres</h1>
          <p className="mt-2 text-sm text-gray-600">
            Votre compte n'a pas les permissions nécessaires pour accéder aux paramètres.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Paramètres</h1>
        <p className="mt-1 text-sm text-gray-600">
          {isGlobalUser
            ? 'Configuration globale et paramètres des tenants accessibles à votre rôle'
            : 'Configuration du tenant associé à votre compte'}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  w-full flex items-center px-3 py-2 text-sm font-medium rounded-md
                  ${selectedTab === tab.id
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                <tab.icon className="h-5 w-5 mr-3" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <div className="card">
            <div className="p-6">
              {selectedTab === 'general' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Paramètres généraux</h3>
                  
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Devise {currency}
                      </label>
                      <select
                        value={hasConfiguredCurrency ? currency : ''}
                        onChange={(e) => handleSettingChange('general', 'currency', e.target.value)}
                        className="input-field mt-1"
                        disabled={currencyOptions.length === 0}
                      >
                        <option value="" disabled>
                          {currencyOptions.length === 0
                            ? "Créez d'abord une devise dans l'onglet Devises"
                            : 'Sélectionnez une devise'}
                        </option>
                        {currencyOptions.map((devise) => (
                          <option key={devise.id} value={devise.name}>
                            {devise.name}
                          </option>
                        ))}
                      </select>
                      {!hasConfiguredCurrency && currency && currencyOptions.length > 0 && (
                        <p className="mt-2 text-xs text-amber-600">
                          La devise actuelle ({currency}) n'existe pas encore dans l'onglet Devises.
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Langue
                      </label>
                      <select
                        value={settings?.general?.language || 'fr'}
                        onChange={(e) => handleSettingChange('general', 'language', e.target.value)}
                        className="input-field mt-1"
                      >
                        <option value="fr">Français</option>
                        <option value="en">English</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Fuseau horaire
                      </label>
                      <select
                        value={settings?.general?.timezone || 'America/Port-au-Prince'}
                        onChange={(e) => handleSettingChange('general', 'timezone', e.target.value)}
                        className="input-field mt-1"
                      >
                        {timezoneOptions.map((timezone) => (
                          <option key={timezone.value} value={timezone.value}>
                            {timezone.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Format de date
                      </label>
                      <select
                        value={settings?.general?.date_format || 'dd/MM/yyyy'}
                        onChange={(e) => handleSettingChange('general', 'date_format', e.target.value)}
                        className="input-field mt-1"
                      >
                        <option value="dd/MM/yyyy">DD/MM/YYYY</option>
                        <option value="MM/dd/yyyy">MM/DD/YYYY</option>
                        <option value="yyyy-MM-dd">YYYY-MM-DD</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {selectedTab === 'finance' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Paramètres financiers</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Taux d'intérêt par défaut (%)
                      </label>
                      <input
                        type="number"
                        value={settings?.finance?.default_interest_rate ?? ''}
                        onChange={(e) => handleSettingChange('finance', 'default_interest_rate', parseFloat(e.target.value))}
                        className="input-field mt-1"
                        min="0"
                        max="50"
                        step="0.1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Taux de pénalité (%)
                      </label>
                      <input
                        type="number"
                        value={settings?.finance?.penalty_rate ?? ''}
                        onChange={(e) => handleSettingChange('finance', 'penalty_rate', parseFloat(e.target.value))}
                        className="input-field mt-1"
                        min="0"
                        max="10"
                        step="0.1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Solde minimum épargne ({currency})
                      </label>
                      <input
                        type="number"
                        value={settings?.finance?.minimum_savings_balance ?? ''}
                        onChange={(e) => handleSettingChange('finance', 'minimum_savings_balance', parseInt(e.target.value))}
                        className="input-field mt-1"
                        min="0"
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings?.finance?.auto_calculate_interest || true}
                        onChange={(e) => handleSettingChange('finance', 'auto_calculate_interest', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        Calcul automatique des intérêts
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {selectedTab === 'currencies' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">Devises</h3>
                    <button type="button" onClick={resetDeviseForm} className="btn-secondary">
                      Nouvelle devise
                    </button>
                  </div>

                  <div className="rounded-lg border border-gray-200 p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-4">
                      {editingDeviseId ? 'Modifier la devise' : 'Créer une devise'}
                    </h4>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Code *</label>
                        <input
                          className="input-field mt-1 uppercase"
                          value={deviseForm.name}
                          onChange={(e) => setDeviseForm({ ...deviseForm, name: e.target.value })}
                          placeholder="HTG"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Pays</label>
                        <input
                          className="input-field mt-1"
                          value={deviseForm.pays}
                          onChange={(e) => setDeviseForm({ ...deviseForm, pays: e.target.value })}
                          placeholder="Haïti"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <input
                          className="input-field mt-1"
                          value={deviseForm.description}
                          onChange={(e) => setDeviseForm({ ...deviseForm, description: e.target.value })}
                          placeholder="Gourde haitienne"
                        />
                      </div>
                      <label className="flex items-center text-sm text-gray-700">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          checked={deviseForm.isLocale}
                          onChange={(e) => setDeviseForm({ ...deviseForm, isLocale: e.target.checked })}
                        />
                        <span className="ml-2">Définir comme devise locale</span>
                      </label>
                    </div>
                    <div className="mt-4 flex justify-end gap-3">
                      {editingDeviseId && (
                        <button type="button" onClick={resetDeviseForm} className="btn-secondary">
                          Annuler
                        </button>
                      )}
                      <button type="button" onClick={handleSaveDevise} className="btn-primary">
                        {editingDeviseId ? 'Mettre à jour' : 'Créer la devise'}
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pays</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Locale</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {devises.map((devise) => (
                          <tr key={devise.id}>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{devise.name}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{devise.description || '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{devise.pays || '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{devise.isLocale ? 'Oui' : 'Non'}</td>
                            <td className="px-4 py-3 text-right text-sm">
                              <button type="button" onClick={() => handleEditDevise(devise)} className="text-primary-600 hover:text-primary-900 mr-3">
                                Modifier
                              </button>
                              <button type="button" onClick={() => handleDeleteDevise(devise.id)} className="text-red-600 hover:text-red-900">
                                Supprimer
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {selectedTab === 'loan-products' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">Produits de prêt</h3>
                    <button type="button" onClick={resetProductForm} className="btn-secondary">
                      Nouveau produit
                    </button>
                  </div>

                  <div className="rounded-lg border border-gray-200 p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-4">
                      {editingProductId ? 'Modifier le produit' : 'Créer un produit de prêt'}
                    </h4>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Nom *</label>
                        <input className="input-field mt-1" value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} placeholder="Prêt commerce" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Code *</label>
                        <input className="input-field mt-1 uppercase" value={productForm.code} onChange={(e) => setProductForm({ ...productForm, code: e.target.value })} placeholder="COM" disabled={!!editingProductId} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Montant minimum ({currency}) *</label>
                        <input type="number" className="input-field mt-1" value={productForm.min_amount} onChange={(e) => setProductForm({ ...productForm, min_amount: e.target.value })} min="0" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Montant maximum ({currency}) *</label>
                        <input type="number" className="input-field mt-1" value={productForm.max_amount} onChange={(e) => setProductForm({ ...productForm, max_amount: e.target.value })} min="0" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Taux d'intérêt (%) *</label>
                        <input type="number" step="0.1" className="input-field mt-1" value={productForm.interest_rate} onChange={(e) => setProductForm({ ...productForm, interest_rate: e.target.value })} min="0" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Type d'intérêt *</label>
                        <select className="input-field mt-1" value={productForm.interest_type} onChange={(e) => setProductForm({ ...productForm, interest_type: e.target.value })}>
                          <option value="flat">Fixe</option>
                          <option value="reducing_balance">Solde dégressif</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Durée min. (mois) *</label>
                        <input type="number" className="input-field mt-1" value={productForm.term_min} onChange={(e) => setProductForm({ ...productForm, term_min: e.target.value })} min="1" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Durée max. (mois) *</label>
                        <input type="number" className="input-field mt-1" value={productForm.term_max} onChange={(e) => setProductForm({ ...productForm, term_max: e.target.value })} min="1" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Période de grâce (jours)</label>
                        <input type="number" className="input-field mt-1" value={productForm.grace_period} onChange={(e) => setProductForm({ ...productForm, grace_period: e.target.value })} min="0" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Taux de pénalité (%)</label>
                        <input type="number" step="0.1" className="input-field mt-1" value={productForm.penalty_rate} onChange={(e) => setProductForm({ ...productForm, penalty_rate: e.target.value })} min="0" />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <textarea className="input-field mt-1" rows={2} value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} />
                      </div>
                      <label className="flex items-center text-sm text-gray-700">
                        <input type="checkbox" className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" checked={productForm.is_active} onChange={(e) => setProductForm({ ...productForm, is_active: e.target.checked })} />
                        <span className="ml-2">Produit actif</span>
                      </label>
                    </div>
                    <div className="mt-4 flex justify-end gap-3">
                      {editingProductId && <button type="button" onClick={resetProductForm} className="btn-secondary">Annuler</button>}
                      <button type="button" onClick={handleSaveLoanProduct} className="btn-primary">
                        {editingProductId ? 'Mettre à jour' : 'Créer le produit'}
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produit</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montants</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Taux</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durée</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {loanProducts.map((product) => (
                          <tr key={product.id}>
                            <td className="px-4 py-3 text-sm">
                              <div className="font-medium text-gray-900">{product.name}</div>
                              <div className="text-gray-500">{product.code}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{formatCurrency(product.min_amount)} - {formatCurrency(product.max_amount)}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{product.interest_rate}%</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{product.term_min} - {product.term_max} mois</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{product.is_active ? 'Actif' : 'Inactif'}</td>
                            <td className="px-4 py-3 text-right text-sm">
                              <button type="button" onClick={() => handleEditLoanProduct(product)} className="text-primary-600 hover:text-primary-900 mr-3">Modifier</button>
                              <button type="button" onClick={() => handleDeleteLoanProduct(product.id)} className="text-red-600 hover:text-red-900">Supprimer</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {selectedTab === 'security' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Paramètres de sécurité</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Politique de mot de passe
                      </label>
                      <select
                        value={settings?.security?.password_policy || 'medium'}
                        onChange={(e) => handleSettingChange('security', 'password_policy', e.target.value)}
                        className="input-field mt-1"
                      >
                        <option value="low">Faible (6 caractères)</option>
                        <option value="medium">Moyenne (8 caractères, lettres et chiffres)</option>
                        <option value="high">Élevée (10 caractères, mixte, caractères spéciaux)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Durée de session (minutes)
                      </label>
                      <input
                        type="number"
                        value={settings?.security?.session_timeout ?? ''}
                        onChange={(e) => handleSettingChange('security', 'session_timeout', parseInt(e.target.value))}
                        className="input-field mt-1"
                        min="5"
                        max="480"
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings?.security?.two_factor_auth || false}
                        onChange={(e) => handleSettingChange('security', 'two_factor_auth', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        Authentification à deux facteurs
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {selectedTab === 'notifications' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Paramètres de notifications</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="block text-sm font-medium text-gray-900">
                          Notifications par email
                        </label>
                        <p className="text-sm text-gray-500">
                          Recevoir les notifications importantes par email
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings?.notifications?.email_notifications || true}
                        onChange={(e) => handleSettingChange('notifications', 'email_notifications', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <label className="block text-sm font-medium text-gray-900">
                          Notifications SMS
                        </label>
                        <p className="text-sm text-gray-500">
                          Recevoir les alertes importantes par SMS
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings?.notifications?.sms_notifications || false}
                        onChange={(e) => handleSettingChange('notifications', 'sms_notifications', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <label className="block text-sm font-medium text-gray-900">
                          Rapports quotidiens
                        </label>
                        <p className="text-sm text-gray-500">
                          Recevoir un rapport quotidien d'activité
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings?.notifications?.daily_reports || true}
                        onChange={(e) => handleSettingChange('notifications', 'daily_reports', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <label className="block text-sm font-medium text-gray-900">
                          Alertes de retard
                        </label>
                        <p className="text-sm text-gray-500">
                          Recevoir des alertes pour les prêts en retard
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings?.notifications?.overdue_alerts || true}
                        onChange={(e) => handleSettingChange('notifications', 'overdue_alerts', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                    </div>
                  </div>
                </div>
              )}

              {selectedTab === 'access' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Accès et permissions</h3>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="rounded-lg border border-gray-200 p-4">
                      <h4 className="text-sm font-medium text-gray-900">Utilisateur connecté</h4>
                      <dl className="mt-3 space-y-2 text-sm">
                        <div className="flex justify-between">
                          <dt className="text-gray-500">Nom</dt>
                          <dd className="font-medium text-gray-900">{user?.first_name} {user?.last_name}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-gray-500">Rôle</dt>
                          <dd className="font-medium text-gray-900">{user?.role?.name || 'Non défini'}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-gray-500">Portée</dt>
                          <dd className="font-medium text-gray-900">{isGlobalUser ? 'Globale' : 'Tenant'}</dd>
                        </div>
                      </dl>
                    </div>

                    <div className="rounded-lg border border-gray-200 p-4">
                      <h4 className="text-sm font-medium text-gray-900">Droits disponibles</h4>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {[
                          ['Paramètres', canManageSettings],
                          ['Utilisateurs', canManageUsers],
                          ['Agences', canManageAgencies],
                          ['Import / Export', canImportExport],
                          ['Global', isGlobalUser],
                        ].map(([label, allowed]) => (
                          <span
                            key={label}
                            className={`badge ${allowed ? 'badge-success' : 'badge-warning'}`}
                          >
                            {label}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {canManageUsers && (
                      <Link to="/users" className="btn-secondary justify-center">
                        Gérer les utilisateurs
                      </Link>
                    )}
                    {canManageAgencies && (
                      <Link to="/agencies" className="btn-secondary justify-center">
                        Gérer les agences
                      </Link>
                    )}
                  </div>
                </div>
              )}

              {selectedTab === 'backup' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Importation et exportation</h3>
                  
                  <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <CloudArrowUpIcon className="h-5 w-5 text-blue-400" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-blue-800">
                            Fichier Excel structuré
                          </h3>
                          <p className="text-sm text-blue-700 mt-1">
                            Téléchargez le modèle, remplissez les onglets, puis importez le fichier complété.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Type d'institution pour le modèle Excel
                      </label>
                      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
                        <select
                          value={selectedTemplateType}
                          onChange={(event) => setSelectedTemplateType(event.target.value)}
                          className="input-field"
                        >
                          {institutionTemplateOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={handleDownloadTemplate}
                          disabled={exporting}
                          className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <DocumentTextIcon className="h-5 w-5 mr-2 text-gray-400" />
                          Télécharger le modèle
                        </button>
                      </div>
                      <p className="mt-2 text-sm text-gray-500">
                        Le fichier généré contient uniquement la structure attendue, sans donnée fictive.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <button
                        onClick={handleExport}
                        disabled={exporting}
                        className="flex items-center justify-center px-4 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                        Exporter Excel
                      </button>

                      <button
                        onClick={handleBackup}
                        className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <ArrowPathIcon className="h-5 w-5 mr-2 text-gray-400" />
                        Sauvegarde manuelle
                      </button>
                    </div>

                    <div className="border-t border-gray-200 pt-6">
                      <h4 className="text-sm font-medium text-gray-900 mb-4">Importer un fichier Excel</h4>
                      <div className="space-y-4">
                        <input
                          type="file"
                          accept=".xlsx"
                          onChange={(e) => {
                            setSelectedImportFile(e.target.files?.[0] || null);
                            setImportResult(null);
                          }}
                          className="input-field"
                        />
                        <button
                          onClick={handleImport}
                          disabled={importing || !selectedImportFile}
                          className="btn-primary"
                        >
                          <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                          {importing ? 'Importation...' : 'Importer les données'}
                        </button>

                        {importResult && (
                          <div className={`rounded-lg border p-4 ${
                            importResult.errors?.length
                              ? 'bg-red-50 border-red-200'
                              : 'bg-green-50 border-green-200'
                          }`}>
                            <p className={`text-sm font-medium ${
                              importResult.errors?.length ? 'text-red-800' : 'text-green-800'
                            }`}>
                              {importResult.message}
                            </p>
                            <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
                              <div>Créées: {importResult.created || 0}</div>
                              <div>Mises à jour: {importResult.updated || 0}</div>
                              <div>Ignorées: {importResult.skipped || 0}</div>
                            </div>
                            {importResult.errors?.length > 0 && (
                              <div className="mt-3 max-h-48 overflow-y-auto text-sm text-red-700">
                                {importResult.errors.slice(0, 20).map((error, index) => (
                                  <p key={index}>
                                    {error.sheet} ligne {error.row}: {error.message}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Save Button */}
              {selectedTab === 'global' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Paramètres globaux</h3>
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <p className="text-sm text-blue-800">
                      Votre rôle est reconnu comme global. Les actions disponibles ici s'appliquent comme supervision:
                      gestion des tenants, permissions globales et exports. Les endpoints globaux dédiés pourront être ajoutés ici
                      lorsque le backend aura les tables et routes correspondantes.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Link to="/users" className="btn-secondary justify-center">
                      Utilisateurs
                    </Link>
                    <Link to="/settings?tab=backup" className="btn-primary justify-center">
                      Import / Export
                    </Link>
                  </div>
                </div>
              )}

              {canManageSettings && !['backup', 'access', 'global', 'currencies', 'loan-products'].includes(selectedTab) && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex justify-end">
                    <button
                      onClick={handleSaveSettings}
                      disabled={saving}
                      className="btn-primary"
                    >
                      {saving ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sauvegarde...
                        </>
                      ) : (
                        'Sauvegarder les paramètres'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantSettings;
